import { FormField } from '@/components/ui/form'
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form'
import Select from '../Select'
import { TimePicker } from '@/components/ui/time-picker'
import FieldWrapper from './FieldWrapper'
import { useContext, useMemo, useState } from 'react'
import { FormEntityContext } from '../../config/context/forms'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CronExpression, CronExpressionParser } from 'cron-parser'
import { Link } from 'react-router-dom'
import cronstrue from 'cronstrue'

interface Props {
  name: string
  shortestHoursInterval: number
}

export const dayOptions = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
].map((day, i) => ({ label: day, value: i.toString(), sortLabel: '' }))

export const defaultCronSchedule = '30 20 * * 2'

export const cronErrors = (cron: string, shortestHoursInterval: number) => {
  const getShortestHoursInterval = (expression: CronExpression) => {
    let prev = expression.next()
    let shortest = Infinity

    Array.from({ length: 200 / shortestHoursInterval }).forEach(() => {
      const next = expression.next()
      const diff = next.getTime() - prev.getTime()
      if (diff < shortest) shortest = diff
      prev = next
    })

    return shortest / 3600000.0
  }

  if (!cron) return 'Enter a cron expression'

  try {
    const parsed = CronExpressionParser.parse(`0 ${cron}`, { strict: true })

    if (getShortestHoursInterval(parsed) < 8) {
      return "Notifications can't be sent more than once every 8 hours"
    }

    return
  } catch (err: unknown) {
    if (err instanceof Error) {
      return err.message.replace('expected 6 fields', 'expected 5 fields')
    }
    return 'Invalid cron expression'
  }
}

const normalizeBasicCron = (cron: string) => {
  try {
    const parsed = CronExpressionParser.parse(`0 ${cron}`, { strict: true })
    const date = parsed.next()

    return `${date.getMinutes()} ${date.getHours()} * * ${date.getDay()}`
  } catch {
    return
  }
}

const CronField = ({ name, shortestHoursInterval }: Props) => {
  const { t } = useTranslation()
  const form = useFormContext()
  const [advancedView, setAdvancedView] = useState(false)
  const { entityType, i18nPrefix } = useContext(FormEntityContext)

  const cron: string = form.watch(name)

  const hasErrors = !!form.formState.errors[name]

  const handleWeekdayChange = (
    field: ControllerRenderProps<FieldValues, string>,
    value?: string,
  ) => {
    const splitCron = cron?.split(' ') ?? []
    const newCron = Array.from({ length: 5 })
      .map((_, i) => {
        if (i === 4) {
          return value ?? '*'
        }

        return splitCron[i] ?? '*'
      })
      .join(' ')

    field.onChange(newCron)
  }

  const handleTimeChange = (
    field: ControllerRenderProps<FieldValues, string>,
    value?: string,
  ) => {
    const splitTime = value?.split(':') ?? []
    const splitCron = cron?.split(' ') ?? []
    const newCron = Array.from({ length: 5 })
      .map((_, i) => {
        if (i === 0) {
          return parseInt(splitTime[1] ?? '0', 10)
        } else if (i === 1) {
          return parseInt(splitTime[0] ?? '0', 10)
        }

        return splitCron[i] ?? '*'
      })
      .join(' ')

    field.onChange(newCron)
  }

  const description = useMemo(() => {
    const error = cronErrors(cron, shortestHoursInterval)
    if (error) return error

    return cronstrue.toString(cron)
  }, [cron])

  return (
    <div className='w-full'>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          return (
            <FieldWrapper
              description={hasErrors ? '' : description}
              label={
                <div className='flex items-end justify-between w-full'>
                  <span className='mb-1 flex gap-1 items-center'>
                    {t(`${i18nPrefix}.${entityType}.${name}.label`)}
                    {advancedView ? (
                      <Link
                        to='https://crontab.guru/'
                        target='_blank'
                        rel='noreferrer'
                        className='underline'
                      >
                        cron
                      </Link>
                    ) : null}
                  </span>
                  <div className='flex items-end'>
                    <Button
                      type='button'
                      size='sm'
                      variant='link'
                      className={`flex items-end bg-transparent hover:bg-transparent hover:no-underline ${
                        advancedView
                          ? 'text-foreground opacity-70 hover:opacity-100'
                          : ''
                      }`}
                      onClick={() => {
                        const basicCron =
                          normalizeBasicCron(field.value) ?? defaultCronSchedule

                        field.onChange(basicCron)
                        setAdvancedView(false)
                      }}
                    >
                      {t('shared.basic')}
                    </Button>
                    <span className='mb-1 mx-[-3px] text-foreground'>/</span>
                    <Button
                      type='button'
                      size='sm'
                      variant='link'
                      className={`flex items-end bg-transparent hover:bg-transparent hover:no-underline ${
                        advancedView
                          ? ''
                          : 'text-foreground opacity-70 hover:opacity-100'
                      }`}
                      onClick={() => setAdvancedView(true)}
                    >
                      {t('shared.advanced')}
                    </Button>
                  </div>
                </div>
              }
            >
              {advancedView ? (
                <Input
                  type={'text'}
                  className='text-lg'
                  placeholder={t(
                    `${i18nPrefix}.${entityType}.${name}.placeholder`,
                  )}
                  {...field}
                />
              ) : (
                <div className='flex gap-4 items-center'>
                  <Select
                    value={cron?.split(' ')[4]}
                    options={dayOptions}
                    placeholder='Select day'
                    setValue={(value) => handleWeekdayChange(field, value)}
                  />
                  <span>at</span>
                  <TimePicker
                    value={cron?.split(' ').slice(0, 2).reverse().join(':')}
                    onChange={(value) => handleTimeChange(field, value)}
                  />
                </div>
              )}
            </FieldWrapper>
          )
        }}
      />
    </div>
  )
}

export default CronField
